import { Link } from "react-router-dom";
import { Star, StarOff } from "lucide-react";
import type { BoursoUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatNumber, fromNowFR } from "@/lib/utils";

type Props = {
  user: BoursoUser;
  isFollowed: boolean;
  onToggleFollow: () => void;
};

/**
 * UserCard — mobile row for the /users list.
 *
 *   ┌───────────────────────────────────────────────┐
 *   │ username                          [follow]   │
 *   │ slug                                          │
 *   ├───────────────────────────────────────────────┤
 *   │  Messages    Abonnés    Vu                    │
 *   │  1 234       42         il y a 2h             │
 *   └───────────────────────────────────────────────┘
 */
export function UserCard({ user, isFollowed, onToggleFollow }: Props) {
  return (
    <article className="rounded-lg border border-border bg-surface p-4 flex flex-col gap-3 transition-colors hover:border-border-strong">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Link
            to={`/users/${user.id}`}
            className="block font-semibold text-foreground hover:text-primary leading-tight truncate"
          >
            {user.username}
          </Link>
          {user.slug && user.slug !== user.username && (
            <span className="text-xs text-foreground-subtle">{user.slug}</span>
          )}
        </div>
        <Button
          size="icon"
          variant={isFollowed ? "default" : "outline"}
          onClick={onToggleFollow}
          aria-label={isFollowed ? "Ne plus suivre" : "Suivre"}
          className="shrink-0"
        >
          {isFollowed ? <Star className="size-4" /> : <StarOff className="size-4" />}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-2 -mx-1 border-t border-border pt-3">
        <Metric label="Messages" value={formatNumber(user.posts_count)} />
        <Metric label="Abonnés" value={formatNumber(user.followers_count ?? 0)} />
        <Metric label="Vu" value={fromNowFR(user.last_seen_at)} subtle />
      </div>
    </article>
  );
}

function Metric({
  label,
  value,
  subtle,
}: {
  label: string;
  value: React.ReactNode;
  subtle?: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5 px-1 min-w-0">
      <span className="text-[10px] uppercase tracking-wide text-foreground-subtle truncate">
        {label}
      </span>
      <span
        className={
          subtle
            ? "text-xs text-foreground-muted truncate"
            : "text-base font-semibold tabular-nums text-foreground truncate"
        }
      >
        {value}
      </span>
    </div>
  );
}
